import { Action } from "redux";

import { LOC_KEY_QUICK_ACCESS_MENU_FAVORITES, LOC_KEY_QUICK_ACCESS_MENU_HISTORY_ITEMS, LOC_KEY_QUICK_ACCESS_MENU_OPENED_ITEMS } from "../locKeys";
import { SEARCHED_TEXT_CHANGED, SET_SEARCH_RESULTS } from "./constants";

/**
 * Reprezentuje stav menu sub modulu pro vyhledávání.
 */
export interface ISearchState {
    /** Text vyhledávacího pole. */
    readonly searchedText: string;
    /** Výsledky vyhledávání. */
    readonly searchResults: ReadonlyArray<SearchResult>;
}

/**
 * Typ výsledku vyhledávání. Rozlišuje formuláře a profily.
 * Slouží k vybrání správné komponenty pro zobrazení výsledků vyhledávání.
 */
export enum SearchResultType {
    /** Vyhledaná položka je formulář. */
    form = "form",
    /** Vyhledaná položka je profil. */
    profile = "profile",
    /** Otevřený formulář. */
    formInstance = "formInstance",
    /** Nadpis skupiny výsledků. */
    groupHeader = "groupHeader",
}

/**
 * Skupina vyhledávání. Pro rozlišení skupin se využívají lokalizační klíče. Pro statické sekce jsou použity konkrétní lokalizační klíče.
 * Pro dynamické sekce se používají lokalizační klíče získané z dat. Dynamické sekce se používají např. při vyhledávání ve formulářích, které
 * jsou seskupeny podle toho, ke kterému kořenovému formuláři patří.
 */
export type SearchGroup =
    typeof LOC_KEY_QUICK_ACCESS_MENU_OPENED_ITEMS |
    typeof LOC_KEY_QUICK_ACCESS_MENU_HISTORY_ITEMS |
    typeof LOC_KEY_QUICK_ACCESS_MENU_FAVORITES |
    string;

/**
 * Výsledek vyhledávání. Slouží ke sjednocení výsledků vyhledávání pro účely zobrazení pomocí virtualizovaného listu.
 */
export type SearchResult = {
    /** Id formuláře nebo profilu nebo otevřené formuláře. Pro typ nadpis skupiny se jedná o lokalizační klíč. */
    readonly id: number | string;
    /** Text vyhledané položky. Slouží pro řazení výsledků. */
    readonly text: string;
    /** Typ výsledku vyhledávání. Rozlišuje formuláře, profily, otevřené formuláře a nadpisy skupin. */
    readonly type: SearchResultType;
    /** Určuje skupinu vyhledávání, do které položka patří. Jedna položka může být vyhledána ve více skupinách, skupina je proto třeba k sestavení unikátního klíče. */
    readonly searchGroup: SearchGroup;
};

/**
 * Struktura dat pro vyhledávání a řazení vyhledaných výsledků.
 */
export type SearchData = {
    id: number | string;
    text: string;
    upperCaseAccentsFreeText: string;
    upperCaseAccentsFreeFormCode: string;
    sourceType: SearchResultType;
};

/**
 * Reprezentuje množinu ids profilů a formulářů nacházejících se v podstormě formulářů.
 */
export type SubtreeIds = {
    readonly formItemIds: readonly number[];
    readonly profileIds: readonly number[];
};

export interface ISearchedTextChangedAction extends Action<typeof SEARCHED_TEXT_CHANGED> {
    readonly payload: {
        searchedText: string;
    };
}

export class ISearchedTextChangedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SEARCHED_TEXT_CHANGED;
    }
}

export interface ISetSearchResultsAction extends Action<typeof SET_SEARCH_RESULTS> {
    readonly payload: {
        readonly searchResults: ReadonlyArray<SearchResult>;
    };
}

export class ISetSearchResultsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_SEARCH_RESULTS;
    }
}
